import {
  SHAutocomplete,
  SHCheckbox,
  SHStack,
  SHTextFieldProps,
  SHTypography,
} from "@components/design-systems";
import { EssentialStarSVG } from "@components/svgs";
import { useInvestment } from "@hooks/useInvestment";
import { useManagedAccount } from "@hooks/useManagedAccount";
import {
  InvestmentQueryOptions,
  InvestmentSelectionDTO,
  SelectedInvestmentDTO,
} from "@models/managed-accounts/entities/step/setup";
import { compareSearchValues } from "@pages/managed-accounts/util";
import { isEmpty } from "lodash";
import React, {
  cloneElement,
  forwardRef,
  ReactElement,
  useEffect,
  useState,
} from "react";
import { Virtuoso } from "react-virtuoso";
import { getSetupRowWarningMessage } from "@pages/platform-analysis/_id/steps/fee/calculator/components/portfolio-setup/utils";
import { AnalysisFeePortfolioWarningIcon } from "@pages/platform-analysis/_id/steps/fee/calculator/components/portfolio-setup/warning-icon";
import { BalanceAvailability } from "@models/platform-analysis/enums/fee/balanceAvailability";

export interface SelectInvestmentsProps {
  openOnFocus?: boolean;
  selectedCodes: string[] | null;
  disabled?: boolean;
  textFieldProps?: Omit<SHTextFieldProps, "value">;
  platformAnalysisId?: string;
  subProductId?: string;
  isGettingPlatformAnalysisInvestments?: boolean;
  calculatedTotal?: {
    idps: number;
    super: number;
  };
  hasIDPSBalance?: boolean;
  hasSuperBalance?: boolean;
  onRemove: (shCode: string) => void;
  onRemoveAll: () => void;
  onAppend: (value: SelectedInvestmentDTO) => void;
  hasChange?: (value: boolean) => void;
  onLoaded?: (response?: InvestmentSelectionDTO[]) => void;
}

export const SelectInvestments = ({
  disabled,
  openOnFocus,
  textFieldProps,
  selectedCodes = [],
  platformAnalysisId,
  subProductId,
  isGettingPlatformAnalysisInvestments,
  calculatedTotal,
  onRemoveAll,
  onAppend,
  onRemove,
  onLoaded,
  hasChange,
  hasIDPSBalance = Number(calculatedTotal?.idps) > 0,
  hasSuperBalance = Number(calculatedTotal?.super) > 0,
}: SelectInvestmentsProps) => {
  const { managedAccountId } = useManagedAccount();
  const {
    loadInvestmentSelection,
    loadPlatformAnalysisInvestmentSelectionsBySubProductId,
    investmentSelections,
    setInvestmentSelection,
  } = useInvestment();

  const [inputValue, setInputValue] = useState("");

  const selectedInvestments = investmentSelections.filter((option) =>
    selectedCodes?.includes(option.shCode),
  );

  const loadData = async (queryOptions: InvestmentQueryOptions) => {
    const gettingPlatformAnalysisInvestments =
      isGettingPlatformAnalysisInvestments &&
      !!subProductId &&
      !!platformAnalysisId;

    const responseData = gettingPlatformAnalysisInvestments
      ? await loadPlatformAnalysisInvestmentSelectionsBySubProductId({
          platformAnalysisId: platformAnalysisId,
          subProductId: subProductId,
          hasSuperBalance,
          hasIDPSBalance,
        })
      : await loadInvestmentSelection(queryOptions);

    if (responseData) setInvestmentSelection(responseData);

    return responseData;
  };

  useEffect(() => {
    const queryOptions: InvestmentQueryOptions = {
      managedAccountId: managedAccountId ?? "new",
      subProductId: subProductId,
    };

    loadData(queryOptions).then((res) => {
      onLoaded && onLoaded(res);
    });
    // eslint-disable-next-line
  }, [managedAccountId]);

  // eslint-disable-next-line
  const ListboxComponent = React.useCallback(
    forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLElement>>(
      (
        props: React.HTMLAttributes<HTMLElement>,
        ref: React.ForwardedRef<HTMLDivElement>,
      ) => {
        const { children, tabIndex, ...others } = props;
        const dataItems = children as ReactElement[];

        const itemHeight = 44;
        const maxItemDisplayed = 6;
        const currentItemCount = dataItems.length;

        const getCurrentHeight = () => {
          if (currentItemCount === 1) return currentItemCount * itemHeight;
          if (currentItemCount >= maxItemDisplayed) {
            return maxItemDisplayed * itemHeight + 10;
          }
          return currentItemCount * itemHeight + 10;
        };

        return (
          <div ref={ref}>
            <Virtuoso
              style={{ height: getCurrentHeight() }}
              data={dataItems}
              itemContent={(index, child) => {
                return cloneElement(child);
              }}
              {...others}
            />
          </div>
        );
      },
    ),
    [],
  );

  return (
    <SHStack spacing={"16px"} direction="column">
      <SHAutocomplete
        multiple
        disabled={disabled}
        disableClearable={false}
        disableCloseOnSelect
        disableListWrap
        isOptionEqualToValue={(option, value) =>
          option.shCode === value?.shCode
        }
        onClose={() => {
          setInputValue("");
        }}
        openOnFocus={openOnFocus}
        inputValue={inputValue}
        value={selectedInvestments ?? []}
        getOptionLabel={(option: InvestmentSelectionDTO) =>
          `${option.displayName} ${option.code}}`
        }
        componentsProps={{
          popper: {
            placement: "top",
          },
        }}
        textFieldProps={{
          ...textFieldProps,
          InputLabelProps: { shrink: true },
          placeholder: selectedInvestments?.length
            ? undefined
            : textFieldProps?.placeholder,
          InputProps: {
            startAdornment: selectedInvestments?.length ? (
              <SHTypography
                disabled={disabled}
                variant="body1"
                sx={{ paddingLeft: "10px" }}
              >
                {`${selectedInvestments?.length} selected`}
              </SHTypography>
            ) : undefined,
          },
          onChange: (e) => {
            setInputValue(e.target.value);
          },
        }}
        onChange={(event: any, newValue, reason, details) => {
          if (
            reason === "removeOption" &&
            event?.type === "keydown" &&
            event?.key === "Backspace"
          ) {
            return;
          }
          if (hasChange) hasChange(true);
          if (isEmpty(newValue)) {
            onRemoveAll();
            return;
          }
          if (
            newValue.some((option) => option.shCode === details?.option.shCode)
          ) {
            onAppend({
              ...details?.option,
              weight: 0,
              value: 0,
              shCode: details?.option?.shCode ?? "",
            });
          } else {
            onRemove(details?.option.shCode ?? "");
          }
        }}
        options={investmentSelections}
        filterOptions={(options, variables) => {
          const searchValue = variables.inputValue?.toLowerCase().trim();
          if (isEmpty(searchValue)) return options;

          const searchValues = searchValue.split(" ").filter(Boolean);

          return options.filter((item) => {
            const dataValues = (item.displayName ?? "")
              .toLowerCase()
              .split(" ")
              .filter(Boolean);

            return compareSearchValues(searchValues, dataValues);
          });
        }}
        renderOption={(props, option, { selected }) => {
          const {
            balanceAvailability,
            isUnavailable,
            shCode,
            code,
            displayName,
            isFavourited,
          } = option || {};
          const warning = getSetupRowWarningMessage({
            balanceAvailability:
              balanceAvailability || BalanceAvailability.Show,
          });

          return (
            <li
              {...props}
              onClick={(e) => {
                if (isUnavailable) return;
                props?.onClick?.(e);
              }}
              key={shCode}
              style={{ padding: "1px 10px" }}
            >
              <SHStack alignItems={"center"} direction="row" spacing={1}>
                <SHCheckbox checked={selected} disabled={isUnavailable} />
                {code && (
                  <SHTypography
                    variant="body3"
                    fontWeight={400}
                    colorVariant="disabled"
                  >
                    {code}
                  </SHTypography>
                )}
                <SHTypography
                  variant="body3"
                  fontWeight={500}
                  disabled={isUnavailable}
                >
                  {displayName}
                </SHTypography>
                {warning && (
                  <AnalysisFeePortfolioWarningIcon
                    type={"warning"}
                    warningData={warning}
                  />
                )}
                {isFavourited && (
                  <EssentialStarSVG
                    fillPath={"#FFF500"}
                    fillRect={"#FF753C"}
                    stroke={"#FF753C"}
                  />
                )}
              </SHStack>
            </li>
          );
        }}
        ListboxComponent={ListboxComponent}
      />
    </SHStack>
  );
};
